import * as React from "react";
import { Play } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { HealthRing, type HealthRingSegment } from "../../../ui/components/HealthRing";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useI18n } from "../../lib/i18n";
import {
  fetchScheduledJobs,
  fetchSmartLinkProductHealth,
  fetchSmartLinkUserHealth,
  formatCategoryCounts,
  runScheduledJob,
  SMART_LINK_RECONCILE_JOBS,
} from "../../lib/api/smart-link";
import type { ScheduledJob, SmartLinkHealth } from "../../lib/api/smart-link";
import * as s from "./SmartLinkHealthSection.css";

const COLORS = {
  inSync: "#16A34A",
  outOfSync: "#DC2626",
  unknown: "#9CA3AF",
  disabled: "#4B5563",
  awaitingLink: "#2563EB",
  unlinkedInSmart: "#D97706",
} as const;

const POLL_MS = 5000;
const POLL_LIMIT = 60;

export interface SmartLinkHealthSectionProps {
  entity: "users" | "products";
  token: string;
  refreshKey: number;
}

export function SmartLinkHealthSection({ entity, token, refreshKey }: SmartLinkHealthSectionProps) {
  const { t } = useI18n();
  const [health, setHealth] = React.useState<SmartLinkHealth | null>(null);
  const [job, setJob] = React.useState<ScheduledJob | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState(false);
  const jobRef = SMART_LINK_RECONCILE_JOBS[entity];

  const load = React.useCallback(async () => {
    try {
      const [nextHealth, jobs] = await Promise.all([
        entity === "users" ? fetchSmartLinkUserHealth(token) : fetchSmartLinkProductHealth(token),
        fetchScheduledJobs(token),
      ]);
      setHealth(nextHealth);
      setJob(jobs.find((j) => j.service === jobRef.service && j.name === jobRef.name) ?? null);
      setError(null);
      return jobs;
    } catch (err: any) {
      setError(err?.message || t("pro.system.smartLink.health.error.load"));
      return null;
    }
  }, [entity, token, jobRef.service, jobRef.name, t]);

  React.useEffect(() => {
    void load();
  }, [load, refreshKey]);

  React.useEffect(() => {
    if (!job?.running) return;
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > POLL_LIMIT) clearInterval(timer);
      void load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [job?.running, load]);

  const handleRunNow = async () => {
    setStarting(true);
    setError(null);
    try {
      await runScheduledJob(token, jobRef.service, jobRef.name);
      await load();
    } catch (err: any) {
      setError(
        err?.status === 409
          ? t("pro.system.smartLink.health.alreadyRunning")
          : err?.message || t("pro.system.smartLink.health.error.run"),
      );
    } finally {
      setStarting(false);
    }
  };

  if (!health) {
    return error ? (
      <Callout variant="error">{error}</Callout>
    ) : (
      <AuthTransitionScreen variant="compact" />
    );
  }

  const segments: HealthRingSegment[] = [
    { key: "inSync", value: health.linked.inSync, color: COLORS.inSync, label: t("pro.system.smartLink.health.legend.inSync", { count: health.linked.inSync }) },
    { key: "outOfSync", value: health.linked.outOfSync, color: COLORS.outOfSync, label: t("pro.system.smartLink.health.legend.outOfSync", { count: health.linked.outOfSync }) },
    { key: "unknown", value: health.linked.unknown, color: COLORS.unknown, label: t("pro.system.smartLink.health.legend.unknown", { count: health.linked.unknown }) },
    { key: "disabled", value: health.linked.disabled, color: COLORS.disabled, label: t("pro.system.smartLink.health.legend.disabled", { count: health.linked.disabled }) },
  ];
  if (health.awaitingLink !== undefined) {
    segments.push({
      key: "awaitingLink",
      value: health.awaitingLink,
      color: COLORS.awaitingLink,
      label: t("pro.system.smartLink.health.legend.awaitingLink", { count: health.awaitingLink }),
    });
  }
  if (health.unlinkedInSmart !== null) {
    segments.push({
      key: "unlinkedInSmart",
      value: health.unlinkedInSmart,
      color: COLORS.unlinkedInSmart,
      label: t(`pro.system.smartLink.health.legend.unlinkedInSmart.${entity}`, { count: health.unlinkedInSmart }),
    });
  }

  const total = segments.reduce((sum, seg) => sum + seg.value, 0);
  const centerLabel = total > 0 ? `${((health.linked.inSync / total) * 100).toFixed(1)}%` : "—";

  return (
    <section className={s.section}>
      <div className={s.header}>
        <Text level="h3" weight="bold">
          {t(`pro.system.smartLink.health.title.${entity}`)}
        </Text>
        <div className={s.reconcile}>
          <span className={s.meta}>
            {job?.running
              ? t("pro.system.smartLink.health.reconciling")
              : health.lastReconciledAt
                ? t("pro.system.smartLink.health.lastReconciled", {
                    when: new Date(health.lastReconciledAt).toLocaleString(),
                  })
                : t("pro.system.smartLink.health.neverReconciled")}
            {job && ` · ${job.schedule}`}
          </span>
          <Button
            type="button"
            size="sm"
            color="neutral"
            variant="outline"
            loading={starting}
            disabled={starting || job?.running}
            iconLeft={<Play />}
            onClick={() => void handleRunNow()}
          >
            {t("pro.system.smartLink.health.runNow")}
          </Button>
        </div>
      </div>

      {health.configured === false && (
        <Callout variant="info">{t("pro.system.smartLink.health.notConfigured")}</Callout>
      )}
      {health.lastReconcileStatus === "failed" && health.lastReconcileError && (
        <Callout variant="error">
          {t("pro.system.smartLink.health.lastRunFailed", { error: health.lastReconcileError })}
        </Callout>
      )}
      {error && <Callout variant="error">{error}</Callout>}

      <div className={s.body}>
        <HealthRing
          segments={segments}
          centerLabel={centerLabel}
          aria-label={t("pro.system.smartLink.health.ringLabel", { pct: centerLabel })}
        />
        <div className={s.details}>
          <div className={s.legend}>
            {segments.map((seg) => (
              <span key={seg.key} className={s.legendItem}>
                <span className={s.swatch} style={{ background: seg.color, borderColor: seg.color }} />
                {seg.label}
              </span>
            ))}
          </div>
          <p className={s.meta}>
            {health.unlinkedInSmart === null && `${t("pro.system.smartLink.health.unlinkedPending")} `}
            {t(`pro.system.smartLink.health.excluded.${entity}`, {
              proOnly: health.excluded.proOnly,
              simulated: health.excluded.simulated,
            })}
          </p>
          {formatCategoryCounts(health.excluded.unsupportedInSmart) && (
            <p className={s.meta}>
              {t("pro.system.smartLink.health.unsupported", {
                categories: formatCategoryCounts(health.excluded.unsupportedInSmart),
              })}
            </p>
          )}
          {health.openFailures > 0 && (
            <p className={s.failures}>
              {t("pro.system.smartLink.health.openFailures", { count: health.openFailures })}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
